import { ReactNode, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { supabase } from '../../lib/supabase'

interface TokenInitializerProps {
  children: ReactNode
}

export function TokenInitializer({ children }: TokenInitializerProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, setIsInitializing, setIsRecoverySession, setIsInviteSession } = useAuth()
  const [isTokenInitialized, setIsTokenInitialized] = useState(false)

  useEffect(() => {
    const initializeToken = async () => {
      setIsInitializing(true)
      
      // Check for special link parameters (recovery or invite)
      const params = new URLSearchParams(location.search)
      const hashParams = new URLSearchParams(location.hash.replace('#', ''))
      const linkType = params.get('type') || hashParams.get('type')
      const isRecoveryLink = linkType === 'recovery'
      const isInviteLink = linkType === 'invite'
      
      // Parse redirect_to parameter to check if it points to reset password page
      const redirectTo = params.get('redirect_to')
      const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : null
      const redirectToResetPassword = decodedRedirectTo && 
        (decodedRedirectTo.includes('/auth/reset-password') || 
         decodedRedirectTo.includes('#/auth/reset-password'))
      
      // Check if we're on the reset password or complete invite page
      const isOnResetPasswordPage = location.pathname === '/auth/reset-password' || 
                                    location.pathname.includes('/auth/reset-password') ||
                                    location.pathname === '/reset-password' ||
                                    location.hash.includes('/auth/reset-password')
      
      const isOnCompleteInvitePage = location.pathname === '/auth/complete-invite' ||
                                     location.pathname.includes('/auth/complete-invite')
      
      // Check for PKCE flow (code parameter) which is used in password reset links
      const code = params.get('code')
      
      // Get legacy tokens from the URL if present
      const accessToken = params.get('access_token') || hashParams.get('access_token')
      const refreshToken = params.get('refresh_token') || hashParams.get('refresh_token')

      // Handle PKCE flow (modern approach used by password reset)
      if (code && !isAuthenticated) {
        console.log('[TokenInitializer] Found PKCE code, recovery link:', isRecoveryLink, 'redirect to reset password:', redirectToResetPassword)
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)

          if (error) throw error

          if (data?.session) {
            console.log('[TokenInitializer] Successfully exchanged code for session')
            
            // Handle recovery links (password reset)
            if (isRecoveryLink || redirectToResetPassword || isOnResetPasswordPage) {
              console.log('[TokenInitializer] Setting recovery session, recovery link:', isRecoveryLink, 'redirect to reset page:', redirectToResetPassword, 'on reset page:', isOnResetPasswordPage)
              setIsRecoverySession(true)
              
              // Navigate to reset password page if not already there
              if (!isOnResetPasswordPage) {
                console.log('[TokenInitializer] Redirecting to reset password page')
                navigate('/auth/reset-password', { replace: true })
                console.log('[TokenInitializer] Navigation to reset password initiated')
              } else {
                console.log('[TokenInitializer] Already on reset password page, not redirecting')
              }
            } 
            // Handle invite links
            else if (isInviteLink) {
              console.log('[TokenInitializer] Detected invitation link')
              // Set invite session state
              setIsInviteSession(true)
              
              // Navigate to complete-invite page if not already there
              if (!isOnCompleteInvitePage) {
                console.log('[TokenInitializer] Redirecting to complete-invite page')
                navigate('/auth/complete-invite', { replace: true })
                console.log('[TokenInitializer] Navigation to complete-invite page initiated')
              } else {
                console.log('[TokenInitializer] Already on complete-invite page, not redirecting')
              }
            }
            else if (decodedRedirectTo) {
              // Follow the redirect_to parameter if it's not a reset password flow
              console.log('[TokenInitializer] Following redirect_to parameter:', decodedRedirectTo)
              const redirectUrl = new URL(decodedRedirectTo, window.location.origin)
              if (redirectUrl.hash) {
                navigate(redirectUrl.hash.replace('#', ''), { replace: true })
              } else {
                navigate(redirectUrl.pathname + redirectUrl.search, { replace: true })
              }
            }
            
            // Clean up the URL by removing the code and other parameters
            const cleanUrl = new URL(window.location.href)
            cleanUrl.searchParams.delete('code')
            cleanUrl.searchParams.delete('type')
            cleanUrl.searchParams.delete('redirect_to')
            window.history.replaceState({}, '', cleanUrl.toString())
          }
        } catch (error) {
          console.error('[TokenInitializer] Failed to exchange code for session:', error)
          navigate('/login')
        } finally {
          setIsTokenInitialized(true)
          setIsInitializing(false)
        }
      }
      // Handle legacy token flow (fallback)
      else if (accessToken && refreshToken && !isAuthenticated) {
        console.log('[TokenInitializer] Found legacy tokens in URL, recovery link:', isRecoveryLink, 'redirect to reset password:', redirectToResetPassword)
        try {
          // Initialize session with both tokens
          const { data: { session }, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })

          if (error) throw error

          if (session) {
            console.log('[TokenInitializer] Successfully initialized session from legacy tokens')
            
            // Handle recovery links (password reset)
            if (isRecoveryLink || redirectToResetPassword || isOnResetPasswordPage) {
              console.log('[TokenInitializer] Setting recovery session from legacy tokens, recovery link:', isRecoveryLink, 'redirect to reset page:', redirectToResetPassword, 'on reset page:', isOnResetPasswordPage)
              setIsRecoverySession(true)
              
              // Navigate to reset password page if not already there
              if (!isOnResetPasswordPage) {
                console.log('[TokenInitializer] Redirecting to reset password page')
                navigate('/auth/reset-password', { replace: true })
                console.log('[TokenInitializer] Navigation to reset password initiated from legacy token flow')
              } else {
                console.log('[TokenInitializer] Already on reset password page (legacy token flow), not redirecting')
              }
            }
            // Handle invite links
            else if (isInviteLink) {
              console.log('[TokenInitializer] Detected invitation link from legacy tokens')
              // Set invite session state
              setIsInviteSession(true)
              
              // Navigate to complete-invite page if not already there
              if (!isOnCompleteInvitePage) {
                console.log('[TokenInitializer] Redirecting to complete-invite page from legacy token flow')
                navigate('/auth/complete-invite', { replace: true })
                console.log('[TokenInitializer] Navigation to complete-invite page initiated from legacy token flow')
              } else {
                console.log('[TokenInitializer] Already on complete-invite page (legacy token flow), not redirecting')
              }
            }
            else if (decodedRedirectTo) {
              // Follow the redirect_to parameter if it's not a reset password flow
              console.log('[TokenInitializer] Following redirect_to parameter:', decodedRedirectTo)
              const redirectUrl = new URL(decodedRedirectTo, window.location.origin)
              if (redirectUrl.hash) {
                navigate(redirectUrl.hash.replace('#', ''), { replace: true })
              } else {
                navigate(redirectUrl.pathname + redirectUrl.search, { replace: true })
              }
            }
            
            // Clean up the URL by removing tokens and other parameters
            const cleanUrl = new URL(window.location.href)
            cleanUrl.searchParams.delete('access_token')
            cleanUrl.searchParams.delete('refresh_token')
            cleanUrl.searchParams.delete('type')
            cleanUrl.searchParams.delete('redirect_to')
            // Clean up hash tokens as well
            if (cleanUrl.hash.includes('access_token')) {
              cleanUrl.hash = ''
            }
            window.history.replaceState({}, '', cleanUrl.toString())
          }
        } catch (error) {
          console.error('[TokenInitializer] Failed to initialize session from legacy tokens:', error)
          navigate('/login')
        } finally {
          setIsTokenInitialized(true)
          setIsInitializing(false)
        }
      } else {
        // No tokens or code in URL, just mark as initialized
        setIsTokenInitialized(true)
        setIsInitializing(false)
      }
    }

    initializeToken()
  }, [location, navigate, isAuthenticated, setIsInitializing, setIsRecoverySession])

  // Don't render children until token initialization is complete
  if (!isTokenInitialized) {
    return null
  }

  return <>{children}</>
}